import Axios from "axios";
import {
    ExternalAuthAccountChoiceDto,
    ExternalAuthExchangeResultDto,
    ExternalAuthProvider,
    ExternalAuthStartResultDto,
} from "../API";

export type ExternalAuthFlow = "login" | "link";
export type ExternalAuthProviderGraphqlName = "MICROSOFT" | "GOOGLE" | "APPLE";

export interface ExternalAuthCallbackParams {
    ticket?: string;
    flow?: ExternalAuthFlow;
}

export interface WebExternalIdentity {
    id: number;
    provider: ExternalAuthProvider | ExternalAuthProviderGraphqlName | string;
    providerTenantId?: string;
    verifiedEmail?: string;
    displayName?: string;
    lastUsedAt?: string;
}

export interface WebExternalIdentityLink {
    id: number;
    externalIdentityId: number;
    lastUsedAt?: string;
    externalIdentity: WebExternalIdentity;
}

interface GraphqlResponse<T> {
    data?: T;
    errors?: Array<{message?: string}>;
}

const providerGraphqlNames: Record<
    ExternalAuthProvider,
    ExternalAuthProviderGraphqlName
> = {
    [ExternalAuthProvider.Microsoft]: "MICROSOFT",
    [ExternalAuthProvider.Google]: "GOOGLE",
    [ExternalAuthProvider.Apple]: "APPLE",
};

const providerTitles: Record<ExternalAuthProvider, string> = {
    [ExternalAuthProvider.Microsoft]: "Microsoft",
    [ExternalAuthProvider.Google]: "Google",
    [ExternalAuthProvider.Apple]: "Apple",
};

export const providerToGraphqlName = (
    provider: ExternalAuthProvider,
): ExternalAuthProviderGraphqlName => providerGraphqlNames[provider];

export const externalProviderFromGraphqlName = (
    value?: string | number | null,
): ExternalAuthProvider | undefined => {
    if (typeof value === "number") {
        return Object.values(ExternalAuthProvider).includes(value)
            ? (value as ExternalAuthProvider)
            : undefined;
    }

    const normalized = value?.trim().toUpperCase();
    if (!normalized) return undefined;

    if (normalized === "MICROSOFT") return ExternalAuthProvider.Microsoft;
    if (normalized === "GOOGLE") return ExternalAuthProvider.Google;
    if (normalized === "APPLE") return ExternalAuthProvider.Apple;

    return undefined;
};

export const externalProviderTitle = (
    provider: ExternalAuthProvider | string | number | undefined | null,
): string => {
    const parsed = externalProviderFromGraphqlName(provider);
    if (!parsed) return typeof provider === "string" ? provider : "Vanjska prijava";
    return providerTitles[parsed];
};

const normalizeExternalAuthFlow = (
    value?: string | null,
): ExternalAuthFlow | undefined => {
    const normalized = value?.trim().toLowerCase();
    if (normalized === "login" || normalized === "link") return normalized;
    return undefined;
};

export const parseExternalAuthSearch = (
    search: string,
): ExternalAuthCallbackParams => {
    const params = new URLSearchParams(search);
    return {
        ticket: params.get("ticket") ?? undefined,
        flow: normalizeExternalAuthFlow(params.get("flow")),
    };
};

export const removeExternalAuthSearchParams = (search: string): string => {
    const params = new URLSearchParams(search);
    params.delete("ticket");
    params.delete("flow");
    const nextSearch = params.toString();
    return nextSearch ? `?${nextSearch}` : "";
};

export const buildExternalAuthRedirectUri = (
    origin: string,
    pathname: string,
): string => {
    const url = new URL(pathname || "/", origin);
    return `${url.origin}${url.pathname}`;
};

export const externalAuthGraphqlEndpoint = (
    baseUrl = Axios.defaults.baseURL,
): string => {
    if (typeof baseUrl !== "string") return "/graphql";

    const normalizedBaseUrl = baseUrl.replace(/\/+$/, "");
    if (!normalizedBaseUrl.endsWith("/api")) return "/graphql";

    const rootUrl = normalizedBaseUrl.slice(0, -"/api".length);
    return rootUrl ? `${rootUrl}/graphql` : "/graphql";
};

export const postGraphql = async <TData>(
    query: string,
    variables?: Record<string, unknown>,
): Promise<TData> => {
    const {data} = await Axios.post<GraphqlResponse<TData>>(
        externalAuthGraphqlEndpoint(),
        {
            query,
            variables,
        },
        {
            baseURL: "",
        },
    );

    if (data.errors?.length) {
        throw new Error(
            data.errors
                .map((error) => error.message)
                .filter(Boolean)
                .join("\n") || "GraphQL zahtjev nije uspio.",
        );
    }

    if (!data.data) {
        throw new Error("GraphQL odgovor ne sadrzi podatke.");
    }

    return data.data;
};

export const externalAuthErrorMessage = (
    error: unknown,
    fallback = "Vanjska prijava nije uspjela.",
): string => {
    if (Axios.isAxiosError(error)) {
        const responseData = error.response?.data as any;
        if (typeof responseData === "string") return responseData;
        if (responseData?.message) return responseData.message;
    }
    if (error instanceof Error && error.message) return error.message;
    return fallback;
};

export const loadExternalAuthProviders = async (): Promise<
    ExternalAuthProvider[]
> => {
    const data = await postGraphql<{
        externalAuthProviders: Array<string | number>;
    }>(`
        query ExternalAuthProviders {
            externalAuthProviders
        }
    `);

    return data.externalAuthProviders
        .map(externalProviderFromGraphqlName)
        .filter((provider): provider is ExternalAuthProvider => !!provider);
};

const startExternalAuth = async (
    mutationName: "startExternalLogin" | "startExternalLink",
    provider: ExternalAuthProvider,
    appRedirectUri: string,
): Promise<ExternalAuthStartResultDto> => {
    const data = await postGraphql<Record<typeof mutationName, ExternalAuthStartResultDto>>(
        `
        mutation StartExternalAuth($provider: ExternalAuthProvider!, $appRedirectUri: String) {
            ${mutationName}(provider: $provider, appRedirectUri: $appRedirectUri) {
                url
            }
        }
    `,
        {
            provider: providerToGraphqlName(provider),
            appRedirectUri,
        },
    );

    return data[mutationName];
};

export const startExternalLogin = (
    provider: ExternalAuthProvider,
    appRedirectUri: string,
): Promise<ExternalAuthStartResultDto> =>
    startExternalAuth("startExternalLogin", provider, appRedirectUri);

export const startExternalLink = (
    provider: ExternalAuthProvider,
    appRedirectUri: string,
): Promise<ExternalAuthStartResultDto> =>
    startExternalAuth("startExternalLink", provider, appRedirectUri);

/** Sent instead of a token when a verified email without an Onix account may join the customer ticket portal. */
export interface PortalRegistrationOffer {
    portalRegistrationTicket?: string | null;
    portalRegistrationEmail?: string | null;
    portalRegistrationDisplayName?: string | null;
}

export type ExternalAuthExchangeResult = ExternalAuthExchangeResultDto & PortalRegistrationOffer;

export const exchangeExternalAuthTicket = async (
    ticket: string,
    selectedUserId?: string,
    takeLicense = false,
): Promise<ExternalAuthExchangeResult> => {
    const data = await postGraphql<{
        exchangeExternalAuthTicket: ExternalAuthExchangeResult;
    }>(
        `
        mutation ExchangeExternalAuthTicket(
            $ticket: String!
            $selectedUserId: String
            $takeLicense: Boolean!
        ) {
            exchangeExternalAuthTicket(
                ticket: $ticket
                selectedUserId: $selectedUserId
                takeLicense: $takeLicense
            ) {
                token
                requiresAccountSelection
                portalRegistrationTicket
                portalRegistrationEmail
                portalRegistrationDisplayName
                accountChoices {
                    userId
                    userName
                    companySettingsId
                    companyName
                }
            }
        }
    `,
        {
            ticket,
            selectedUserId,
            takeLicense,
        },
    );

    return data.exchangeExternalAuthTicket;
};

export const loadMyExternalIdentities = async (): Promise<
    WebExternalIdentityLink[]
> => {
    const data = await postGraphql<{
        myExternalIdentities: WebExternalIdentityLink[];
    }>(`
        query MyExternalIdentities {
            myExternalIdentities {
                id
                externalIdentityId
                lastUsedAt
                externalIdentity {
                    id
                    provider
                    providerTenantId
                    verifiedEmail
                    displayName
                    lastUsedAt
                }
            }
        }
    `);

    return data.myExternalIdentities;
};

export const unlinkExternalIdentity = async (
    externalIdentityId: number,
): Promise<boolean> => {
    const data = await postGraphql<{unlinkExternalIdentity: boolean}>(
        `
        mutation UnlinkExternalIdentity($externalIdentityId: Int!) {
            unlinkExternalIdentity(externalIdentityId: $externalIdentityId)
        }
    `,
        {
            externalIdentityId,
        },
    );

    return data.unlinkExternalIdentity;
};

export const externalAuthAccountChoiceTitle = (
    choice: ExternalAuthAccountChoiceDto,
): string =>
    [choice.companyName, choice.userName].filter(Boolean).join(" - ") ||
    choice.userId;
